import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  TOGGLE_NAMES,
  useFeatureToggle,
  useToggleLoadingValue,
} from '~/platform/utilities/feature-toggles';
import InitializeVAPServiceID2Container from './InitializeVAPServiceID2';
import {
  INIT_VAP_SERVICE_ID,
  ANALYTICS_FIELD_MAP,
  API_ROUTES,
  VAP_SERVICE_INITIALIZATION_STATUS,
} from '../constants';
import {
  createTransaction,
  fetchTransactions,
  refreshTransaction,
} from '../actions';
import { selectVAPServiceInitializationStatus } from '../selectors';
import VAPServiceTransactionPending from '../components/base/VAPServiceTransactionPending';
import { TransactionPending } from '../components/base/TransactionPending';

class InitializeVAPServiceID extends React.Component {
  componentDidMount() {
    if (this.props.status === VAP_SERVICE_INITIALIZATION_STATUS.INITIALIZED) {
      this.props.fetchTransactions();
    } else {
      this.initializeVAPServiceID();
    }
  }

  initializeVAPServiceID() {
    const route = API_ROUTES.INIT_VAP_SERVICE_ID;
    const fieldName = INIT_VAP_SERVICE_ID;
    const method = 'POST';
    const body = null;
    const analytics = ANALYTICS_FIELD_MAP.INIT_VAP_SERVICE_ID;
    return this.props.createTransaction(
      route,
      method,
      fieldName,
      body,
      analytics,
    );
  }

  refreshTransaction = () => {
    // "INITIALIZING" is also the status for when the request to create the transaction is pending, so
    // we have to make sure that isn't the case when we try to refresh the transaction because that would
    // means this.props.transaction is null.
    if (this.props.transactionRequest.isPending) return;

    const { transactionId } = this.props.transaction.data.attributes;
    const initializationTransactionRefreshRoute = `/profile/person/status/${transactionId}`;
    this.props.refreshTransaction(
      this.props.transaction,
      ANALYTICS_FIELD_MAP.INIT_VAP_SERVICE_ID,
      initializationTransactionRefreshRoute,
    );
  };

  render() {
    switch (this.props.status) {
      case VAP_SERVICE_INITIALIZATION_STATUS.INITIALIZED:
        return <>{this.props.children}</>;

      case VAP_SERVICE_INITIALIZATION_STATUS.INITIALIZATION_FAILURE:
        return (
          <va-alert visible status="info" uswds>
            We’re sorry. Something went wrong on our end. Please refresh this
            page or try again later.
          </va-alert>
        );

      case VAP_SERVICE_INITIALIZATION_STATUS.INITIALIZING: {
        const PendingTransactionComponent = this.props.useProfileServiceRefactor
          ? TransactionPending
          : VAPServiceTransactionPending;
        return (
          <PendingTransactionComponent
            refreshTransaction={this.refreshTransaction}
          >
            Initialization in progress...
          </PendingTransactionComponent>
        );
      }

      case VAP_SERVICE_INITIALIZATION_STATUS.UNINITIALIZED:
      default:
        return <div />;
    }
  }
}

InitializeVAPServiceID.propTypes = {
  createTransaction: PropTypes.func.isRequired,
  fetchTransactions: PropTypes.func.isRequired,
  refreshTransaction: PropTypes.func.isRequired,
  children: PropTypes.node,
  status: PropTypes.oneOf(Object.values(VAP_SERVICE_INITIALIZATION_STATUS)),
  transaction: PropTypes.shape({
    data: PropTypes.shape({
      attributes: PropTypes.shape({
        transactionId: PropTypes.string,
      }),
    }),
  }),
  transactionRequest: PropTypes.shape({
    isPending: PropTypes.bool,
  }),
  useProfileServiceRefactor: PropTypes.bool,
};

const mapStateToProps = state => {
  const { status, transaction, transactionRequest } =
    selectVAPServiceInitializationStatus(state);
  return {
    status,
    transaction,
    transactionRequest,
    useProfileServiceRefactor:
      state.featureToggles?.[TOGGLE_NAMES.cveProfileServiceRefactor],
  };
};

const mapDispatchToProps = {
  createTransaction,
  fetchTransactions,
  refreshTransaction,
};

/**
 * A Container for initializing the user into VA Profile if they are not
 * already. Otherwise, this container will initialize the Profile app state by
 * fetching all transactions.
 */
const InitializeVAPServiceIDContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(InitializeVAPServiceID);

/**
 * Feature-flag switch between the legacy class-based container and the
 * refactored hooks-based container (InitializeVAPServiceID2). Consumers keep
 * importing this file's default export; the cve_profile_service_refactor
 * toggle decides which implementation renders.
 */
const InitializeVAPServiceIDSwitch = ({ children }) => {
  const { useToggleValue } = useFeatureToggle();
  const useProfileServiceRefactor = useToggleValue(
    TOGGLE_NAMES.cveProfileServiceRefactor,
  );
  // useToggleLoadingValue is deliberately the direct module export rather
  // than the one returned by useFeatureToggle(): consumer specs commonly stub
  // useFeatureToggle() with a partial API ({ useToggleValue, TOGGLE_NAMES }),
  // and destructuring the loading hook from that stub would crash them.
  const togglesLoading = useToggleLoadingValue();

  // Don't render either implementation until toggles resolve — swapping
  // mid-flight would remount the container and re-fire the initialization
  // request against VA Profile.
  if (togglesLoading) return null;

  return useProfileServiceRefactor ? (
    <InitializeVAPServiceID2Container>
      {children}
    </InitializeVAPServiceID2Container>
  ) : (
    <InitializeVAPServiceIDContainer>
      {children}
    </InitializeVAPServiceIDContainer>
  );
};

InitializeVAPServiceIDSwitch.propTypes = {
  children: PropTypes.node,
};

export default InitializeVAPServiceIDSwitch;
export { InitializeVAPServiceID, InitializeVAPServiceIDContainer };
